import React, { useState } from 'react';
import { useRole, COORDINATOR_ROLES, ROLE_LABELS } from './contexts/RoleContext';
import { Users, Package2, Tag, ChevronLeft } from 'lucide-react';

const NicknameModal = ({ nicknames, onSelect }) => {
  const { setCurrentRole } = useRole();
  const [step, setStep] = useState(1); // 1: Select name, 2: Select coordinator count, 3: Select specific role
  const [selectedNickname, setSelectedNickname] = useState(null);
  // Determine grid columns based on number of nicknames
  const getGridCols = (count) => {
    if (count <= 8) return 'grid-cols-2';
    if (count <= 15) return 'grid-cols-3';
    if (count <= 24) return 'grid-cols-4';
    return 'grid-cols-5';
  };

  const handleNicknameClick = (nickname) => {
    setSelectedNickname(nickname);
    setStep(2);
  };

  const handleCoordinatorCountSelect = (count) => {
    if (count === 1) {
      setCurrentRole(COORDINATOR_ROLES.SHIPMENT);
      onSelect(selectedNickname);
    } else {
      setStep(3);
    }
  };

  const handleRoleSelect = (role) => {
    setCurrentRole(role);
    onSelect(selectedNickname);
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      setStep(1);
      setSelectedNickname(null);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full mx-4 overflow-hidden transform transition-all duration-300">
        {step === 1 && (
          <div className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome! 👋</h2>
              <p className="text-gray-600">Please select your name to continue</p>
            </div>
            <div className={`grid ${getGridCols(nicknames.length)} gap-4`}>
              {nicknames.map((nicknameObj, index) => (
                <button
                  key={index}
                  onClick={() => handleNicknameClick(nicknameObj.NICKNAME)}
                  className="group relative bg-gradient-to-br from-slate-50 to-slate-100 hover:from-blue-50 hover:to-blue-100 border-2 border-slate-200 hover:border-blue-300 rounded-xl py-5 px-6 text-base font-semibold text-slate-700 hover:text-blue-700 transition-all duration-200 transform hover:scale-105 hover:shadow-lg"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-200" />
                  <span className="relative z-10">{nicknameObj.NICKNAME}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="relative">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8">
              <button 
                onClick={handleBack}
                className="absolute top-6 left-6 p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-200 group"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
              </button>
              
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  How many coordinators are working today?
                </h2>
                <p className="text-gray-600">
                  Hi <span className="font-bold text-blue-600">{selectedNickname}</span>! 👋 Let us know your workflow
                </p>
              </div>
            </div>
            
            <div className="p-8 space-y-4 max-w-3xl mx-auto">
              <button
                onClick={() => handleCoordinatorCountSelect(1)}
                className="group w-full p-6 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-white bg-opacity-20 rounded-xl group-hover:bg-opacity-30 transition-all duration-200">
                    <Users className="w-8 h-8" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-bold text-xl mb-1">1 Coordinator</h3>
                    <p className="text-blue-100">I'm coordinating both product labels and box contents</p>
                  </div>
                  <div className="text-3xl opacity-50">→</div>
                </div>
              </button>

              <button
                onClick={() => handleCoordinatorCountSelect(2)}
                className="group w-full p-6 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-white bg-opacity-20 rounded-xl group-hover:bg-opacity-30 transition-all duration-200">
                    <Users className="w-8 h-8" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-bold text-xl mb-1">2 Coordinators</h3>
                    <p className="text-purple-100">One coordinator for labels, one for box contents</p>
                  </div>
                  <div className="text-3xl opacity-50">→</div>
                </div>
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="relative">
            <div className="bg-gradient-to-br from-purple-50 to-green-50 p-8">
              <button 
                onClick={handleBack}
                className="absolute top-6 left-6 p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-200 group"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
              </button>
              
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  What's your role today?
                </h2>
                <p className="text-gray-600">
                  Choose your focus area to optimize your workspace
                </p>
              </div>
            </div>
            
            <div className="p-8 space-y-4 max-w-3xl mx-auto">
              <button
                onClick={() => handleRoleSelect(COORDINATOR_ROLES.PRODUCT_LABEL)}
                className="group w-full p-6 rounded-2xl border-3 border-purple-200 bg-white hover:border-purple-400 hover:bg-purple-50 shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-4 bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl text-white shadow-lg group-hover:shadow-xl transition-all duration-200">
                    <Tag className="w-10 h-10" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-bold text-xl mb-2 text-gray-900">Product & Labels</h3>
                    <p className="text-gray-600">I'll handle product details, labels, and expiration dates</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">Print Labels</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">Update Expiry</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">Manage Products</span>
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleRoleSelect(COORDINATOR_ROLES.BOX_CONTENTS)}
                className="group w-full p-6 rounded-2xl border-3 border-green-200 bg-white hover:border-green-400 hover:bg-green-50 shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-4 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl text-white shadow-lg group-hover:shadow-xl transition-all duration-200">
                    <Package2 className="w-10 h-10" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-bold text-xl mb-2 text-gray-900">Box Contents</h3>
                    <p className="text-gray-600">I'll organize and pack items into boxes</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">Pack Items</span>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">Manage Boxes</span>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">Box Dimensions</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NicknameModal;
